/**
 * The book itself, on the device.
 *
 * `books.ts` keeps the shape of a chapter — which numbers exist, their Nivå, their sections. This
 * keeps the PDF, because the owner's verdict on the transcription was that he does not want to
 * read it: he wants the page. So the bytes are the book, the parse is an index into it, and this
 * module holds both together with the one thing that joins them — the regions that say where on
 * the page each exercise is printed.
 *
 * Nothing here uploads and nothing here fetches. `File` → `ArrayBuffer` → IndexedDB is the whole
 * path and there is no network call in this file, which is PLAN.md §6.11 and the reason a course
 * book can be in this app at all.
 *
 * **Its own database, `euclid-book-pdf`.** The spine is at `euclid` v3 and ingest's
 * parse is at `euclid-books` v1; a third upgrade handler racing either of those is how a store
 * gets created twice and records go missing. A separate database costs one `open` and cannot
 * collide.
 *
 * Two stores rather than one, and the split is the point: a 40 MB book must not be read off the
 * disk to draw a list of names. `index` is small and is read constantly; `blobs` is read once,
 * when a document is opened.
 */
import { ref } from 'vue';

import type { Region } from './pdf-regions';

/**
 * One exercise, as the index knows it.
 *
 * Deliberately no prompt. It used to be here, typed out; it is clipped off the page now, and a
 * clip carries the diagrams, the tables and the figures a transcription could only drop. The
 * answer stays, because the facit button reveals it as text and always has.
 */
export interface DocExercise {
  n: number;
  level: 1 | 2 | 3 | 4 | null;
  starred: boolean;
  /** One string, one per lettered part, or null for a "visa att" with nothing to check. */
  answer: string | string[] | null;
  /** The page it starts on, 0-based, so the pane can jump to a number. */
  page: number;
}

export interface DocIndex {
  id: string;
  /** The file as the owner named it. */
  name: string;
  /** The chapter this book belongs to, or '' when it was opened straight onto a scratch sheet. */
  chapterId: string;
  courseId: string;
  pageCount: number;
  pageSizes: { width: number; height: number }[];
  addedAt: number;
  exercises: DocExercise[];
  regions: Region[];
  /** How many exercises got a clickable region, and out of how many. Reported, never assumed. */
  resolved: number;
  total: number;
  /**
   * This one shipped with the app rather than being chosen from a disk.
   *
   * The ONLY thing that differs between a built-in chapter and an uploaded one — everything else
   * about it, including how it was read, is identical on purpose. It is here so the library can
   * mark it and so deleting it can be remembered rather than undone on the next boot.
   */
  builtin?: true;
}

const DB_NAME = 'euclid-book-pdf';
const INDEX = 'index';
const BLOBS = 'blobs';

let handle: Promise<IDBDatabase> | null = null;

/**
 * Bumped whenever the shelf changes.
 *
 * A book arriving is news to anything showing the library, and the install runs off the boot path
 * — so the Book pane could read an empty shelf, be right at the time, and then sit there being
 * wrong. Polling was the first answer and it is the wrong one: the writer knows.
 */
export const shelfRevision = ref(0);

const upgrade = (db: IDBDatabase): void => {
  if (!db.objectStoreNames.contains(INDEX)) {
    db.createObjectStore(INDEX, { keyPath: 'id' }).createIndex('chapterId', 'chapterId');
  }
  if (!db.objectStoreNames.contains(BLOBS)) db.createObjectStore(BLOBS, { keyPath: 'id' });
};

const openAt = (version?: number): Promise<IDBDatabase> => new Promise((resolve, reject) => {
  const request = version === undefined
    ? indexedDB.open(DB_NAME)
    : indexedDB.open(DB_NAME, version);
  request.onupgradeneeded = () => { upgrade(request.result); };
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error);
  // Without this the promise never settles and the pane waits forever with nothing to say.
  request.onblocked = () => reject(new Error('Paint 6000 is open in another tab — close it to finish updating.'));
});

/**
 * The book database, and it repairs itself if it was created by somebody else.
 *
 * `indexedDB.open(name)` with NO version creates the database empty when it does not exist, at
 * version 1 and with no object stores in it. Anything that does that before this app has ever run
 * — another tab, a devtools poke, an older build, a test harness — leaves a version-1 database
 * that this code then opens happily without an upgrade, because 1 is the version it wanted. Every
 * write after that fails with `One of the specified object stores was not found`, forever, and
 * the only symptom is a book that will not install and a console line nobody has open.
 *
 * So the stores are CHECKED rather than assumed, and a database missing them is reopened one
 * version higher, which is what actually runs the upgrade.
 */
export function openPdfDb(): Promise<IDBDatabase> {
  if (handle) return handle;
  handle = (async () => {
    /*
     * Versionless, deliberately.
     *
     * Opening at a CONSTANT version is what breaks the moment the repair below has ever run:
     * `indexedDB.open(name, 1)` against a database now at 2 fails outright with `VersionError`,
     * and the book pane goes empty on every load afterwards. No version means "whatever is there",
     * which still fires `onupgradeneeded` when the database does not exist yet.
     */
    let db = await openAt();
    if (!db.objectStoreNames.contains(INDEX) || !db.objectStoreNames.contains(BLOBS)) {
      const next = db.version + 1;
      db.close();
      db = await openAt(next);
    }
    return db;
  })();
  handle.catch(() => { handle = null; });
  return handle;
}

const run = async <T,>(
  store: string,
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> => {
  const db = await openPdfDb();
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(store, mode);
    const request = work(tx.objectStore(store));
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Strip Vue's reactive proxy without touching the bytes.
 *
 * `storage.ts` and `books.ts` both unwrap with a JSON round trip, and that would be a disaster
 * here: `JSON.parse(JSON.stringify(buffer))` turns an ArrayBuffer into `{}` and the book becomes
 * an empty object with no error anywhere. The index is JSON-shaped and gets the round trip; the
 * bytes are written on their own and never go near it.
 */
const plain = <T,>(record: T): T => JSON.parse(JSON.stringify(record)) as T;

export async function savePdf(index: DocIndex, bytes: ArrayBuffer): Promise<void> {
  await run(INDEX, 'readwrite', store => store.put(plain(index)) as IDBRequest<IDBValidKey>);
  await run(BLOBS, 'readwrite', store => store.put({ id: index.id, bytes }) as IDBRequest<IDBValidKey>);
  shelfRevision.value += 1;
}

export const loadPdfIndex = (id: string): Promise<DocIndex | undefined> =>
  run<DocIndex | undefined>(INDEX, 'readonly', store => store.get(id));

export async function loadPdfBytes(id: string): Promise<ArrayBuffer | null> {
  const record = await run<{ id: string; bytes: ArrayBuffer } | undefined>(
    BLOBS, 'readonly', store => store.get(id));
  return record?.bytes ?? null;
}

/** Every document, newest first. Index records only — the bytes are never touched here. */
export async function listPdfs(): Promise<DocIndex[]> {
  const all = await run<DocIndex[]>(INDEX, 'readonly', store => store.getAll());
  return all.sort((a, b) => b.addedAt - a.addedAt);
}

/** The book a chapter reads from, if one was kept when the chapter was created. */
export async function pdfForChapter(chapterId: string): Promise<DocIndex | null> {
  if (!chapterId) return null;
  const all = await listPdfs();
  return all.find(doc => doc.chapterId === chapterId) ?? null;
}

export async function deletePdf(id: string): Promise<void> {
  await run(INDEX, 'readwrite', store => store.delete(id) as unknown as IDBRequest<undefined>);
  await run(BLOBS, 'readwrite', store => store.delete(id) as unknown as IDBRequest<undefined>);
  shelfRevision.value += 1;
}

// ── What the pane remembers, per sheet ─────────────────────────────────────────────────────

/** The exercise a sheet is working on, as much of it as the facit button needs. */
export interface PaneExercise {
  n: number;
  /** One per lettered part, or one whole answer. Empty means a proof with nothing to check. */
  answers: string[];
  level: 1 | 2 | 3 | 4 | null;
  starred: boolean;
}

/**
 * The split, the document and the page, remembered per SHEET.
 *
 * Per sheet rather than globally because that is what the owner asked for and because it is what
 * makes the pane feel like a book left open: come back to 1042's sheet tomorrow and the page it
 * was open at is still the page. `localStorage` rather than the sheet record — this is where a
 * divider sits, not part of anyone's working, and it must never enter the undo stack or the
 * scene's serialisation.
 */
export interface PaneState {
  open: boolean;
  docId: string;
  /** 0-based, the page the reader was last looking at. */
  page: number;
  /**
   * How far INTO that page, as a fraction of its height.
   *
   * The page alone was not where you were. A chapter page is taller than the pane, so coming back
   * to "page 12" landed at the top of 12 and the exercise being worked was half a screen further
   * down — every time. The owner: *"the PDF re loads fully and you have to scroll all the way down
   * again, make sure it stays put where it was the last time you exited it."*
   *
   * A fraction rather than a pixel offset, so it survives the zoom changing between visits.
   */
  offset: number;
  /** The pane's width in CSS pixels. */
  width: number;
  zoom: number;
  /**
   * The exercise last taken off the page onto this sheet.
   *
   * Remembered with the pane rather than held in memory, so coming back to a scratch sheet
   * tomorrow still knows which exercise it is working on — which is what keeps the facit one
   * small press away instead of something you have to go and find again.
   */
  exercise: PaneExercise | null;
}

export const DEFAULT_PANE: PaneState = {
  open: false, docId: '', page: 0, offset: 0, width: 560, zoom: 1, exercise: null,
};

export const MIN_PANE = 320;
export const MAX_PANE = 1200;

const key = (sheetId: string) => `euclid.book.${sheetId || 'scratch'}`;

export function readPane(sheetId: string): PaneState {
  try {
    const raw = localStorage.getItem(key(sheetId));
    if (!raw) return { ...DEFAULT_PANE };
    const stored = JSON.parse(raw) as Partial<PaneState>;
    return {
      open: stored.open === true,
      docId: typeof stored.docId === 'string' ? stored.docId : '',
      page: Number.isFinite(stored.page) ? Math.max(0, Math.floor(stored.page as number)) : 0,
      offset: Number.isFinite(stored.offset) ? Math.min(1, Math.max(0, stored.offset as number)) : 0,
      width: clampPane(Number(stored.width)),
      zoom: Number.isFinite(stored.zoom) ? Math.min(4, Math.max(0.5, stored.zoom as number)) : 1,
      exercise: stored.exercise && Number.isFinite(stored.exercise.n)
        ? {
            n: stored.exercise.n,
            answers: Array.isArray(stored.exercise.answers) ? stored.exercise.answers.map(String) : [],
            level: stored.exercise.level ?? null,
            starred: stored.exercise.starred === true,
          }
        : null,
    };
  } catch {
    // A private window with storage denied is a working app with an unremembered divider, not a
    // broken one.
    return { ...DEFAULT_PANE };
  }
}

export const clampPane = (width: number): number =>
  (Number.isFinite(width) ? Math.min(MAX_PANE, Math.max(MIN_PANE, width)) : DEFAULT_PANE.width);

/** Carry a sheet's pane state to a new key — used when filing re-keys the scratch pad. */
export function movePane(from: string, to: string): void {
  if (!from || !to || from === to) return;
  const state = readPane(from);
  writePane(to, state);
  try { localStorage.removeItem(key(from)); } catch { /* storage denied */ }
}

export function writePane(sheetId: string, state: PaneState): void {
  try {
    localStorage.setItem(key(sheetId), JSON.stringify(state));
  } catch { /* storage denied; the pane still works, it just forgets. */ }
}
